import React, { memo, useState, useEffect } from 'react';
import './styleItemCardCarrito.css';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { message, Drawer, Typography, InputNumber, Button, Space } from 'antd';

const { Text } = Typography;

const ItemCardCarrito = ({
  id, nombre, numItems, setCarrito, precioVenta, stock, descuento, vaciarCarrito,
  precioCosto = 0, permitirEdicionPrecio = false, controlarStock = false
}) => {

  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [tempPrice, setTempPrice] = useState(0);

  const stockParsed = Number(stock) || 0;
  const numItemsParsed = Number(numItems) || 0;

  const formatMoney = (valor) => {
    return Number.isFinite(Number(valor))
      ? new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Math.round(Number(valor)))
      : "$0";
  };

  const tieneDescuento = descuento > 0;
  const precioUnitarioFinal = tieneDescuento
    ? precioVenta * (1 - descuento / 100)
    : precioVenta;

  useEffect(() => {
    setTempPrice(precioUnitarioFinal);
  }, [precioUnitarioFinal, isEditingPrice]);

  const eliminarCard = () => {
    setCarrito((prevCarrito) => {
      if (prevCarrito.length === 1) {
        setTimeout(vaciarCarrito, 0);
        return [];
      }
      return prevCarrito.filter(item => item.id !== id);
    });
  }

  const handleSavePrice = () => {
    if (tempPrice === null || tempPrice === undefined) {
      return message.warning("Ingresa un precio válido.");
    }

    const nuevoPrecio = Number(tempPrice);
    const costoMinimo = Number(precioCosto);

    if (isNaN(nuevoPrecio) || nuevoPrecio < 0) return;

    if (nuevoPrecio < costoMinimo) {
      message.error(`No puedes vender por debajo del costo (${formatMoney(costoMinimo)})`);
      setTempPrice(precioUnitarioFinal);
      setIsEditingPrice(false);
      return;
    }

    setCarrito((prev) => prev.map(item =>
      item.id === id
        ? {
          ...item,
          precioVenta: nuevoPrecio,
          descuento: 0,
          precio_editado: true
        }
        : item
    ));
    setIsEditingPrice(false);
  };

  const aumentarItems = () => {
    if (controlarStock && numItemsParsed >= stockParsed) {
      message.warning(`Stock máximo alcanzado (${stockParsed} disp.)`);
      return;
    }
    setCarrito((prevCarrito) =>
      prevCarrito.map(item =>
        item.id === id ? { ...item, numItems: Number(item.numItems) + 1 } : item
      )
    );
  }

  const disminuirItems = () => {
    setCarrito((prevCarrito) =>
      prevCarrito.map(item =>
        (item.id === id && item.numItems > 1) ? { ...item, numItems: Number(item.numItems) - 1 } : item
      )
    );
  }

  const handleInputChange = (e) => {
    let valor = parseInt(e.target.value);
    if (!isNaN(valor)) {
      if (valor < 0) valor = 0;
      if (controlarStock && valor > stockParsed) {
        message.warning(`Solo hay ${stockParsed} unidades disponibles`);
        valor = stockParsed;
      }
      setCarrito((prevCarrito) =>
        prevCarrito.map(item =>
          item.id === id ? { ...item, numItems: valor } : item
        )
      );
    }
  }

  return (
    <div className={`pos-item-container ${tieneDescuento ? 'pos-item-promo' : ''}`}>

      {/* FILA 1: Título 100% ancho */}
      <div className="pos-item-header">
        <span className="pos-item-title" title={nombre}>{nombre}</span>
      </div>

      {/* FILA 2: Cantidad - Precios - Acciones */}
      <div className="pos-item-body">

        <div className="pos-qty-touch-control">
          <button disabled={numItems === 1} className="pos-qty-action-btn" onClick={disminuirItems}>−</button>
          <input
            type="number"
            className="pos-qty-number-input"
            value={numItems}
            onChange={handleInputChange}
            onFocus={(e) => e.target.select()}
          />
          <button className="pos-qty-action-btn" onClick={aumentarItems}>+</button>
        </div>

        <div className="pos-item-price-info">
          {tieneDescuento && (
            <span className="pos-item-strike-price">
              Antes: {formatMoney(precioVenta * numItems)}
            </span>
          )}
          <span className="pos-item-final-price">
            {formatMoney(precioUnitarioFinal * numItems)}
          </span>
          <span className="pos-item-unit-text">
            {numItems} x {formatMoney(precioUnitarioFinal)}
          </span>
        </div>

        <div className="pos-item-actions">
          {permitirEdicionPrecio && (
            <button className="pos-action-btn pos-btn-edit" onClick={() => setIsEditingPrice(true)} title="Modificar Precio">
              <EditOutlined />
            </button>
          )}
          <button className="pos-action-btn pos-btn-delete" onClick={eliminarCard} title="Eliminar producto">
            <DeleteOutlined />
          </button>
        </div>

      </div>

      {/* DRAWER LATERAL PARA EDICIÓN DE PRECIO */}
      <Drawer
        title={<Text strong style={{ fontSize: '16px' }}>Modificar Precio</Text>}
        placement="right"
        width={320}
        onClose={() => { setIsEditingPrice(false); setTempPrice(precioUnitarioFinal); }}
        open={isEditingPrice}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Producto Seleccionado</Text>
            <Text strong style={{ fontSize: '16px', color: '#0f172a', lineHeight: '1.2', display: 'block' }}>{nombre}</Text>
          </div>

          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <Space direction="vertical" style={{ width: '100%', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Costo de Compra:</Text>
                <Text strong style={{ color: '#ef4444' }}>{formatMoney(precioCosto)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Precio Actual:</Text>
                <Text strong style={{ color: '#0f172a' }}>{formatMoney(precioUnitarioFinal)}</Text>
              </div>
            </Space>
          </div>

          <div>
            <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '8px', color: '#334155' }}>
              Nuevo Precio de Venta Unitario
            </Text>
            <InputNumber
              className="input-precio-grande"
              style={{ width: '100%', borderRadius: '10px' }}
              prefix={<span style={{ color: '#94a3b8', marginRight: '5px', fontSize: '20px' }}>$</span>}
              autoFocus
              controls={false}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
              parser={value => value.replace(/\$\s?|(\.*)/g, '')}
              value={tempPrice}
              onChange={(val) => setTempPrice(val)}
              onPressEnter={handleSavePrice}
              onFocus={(e) => e.target.select()}
            />
          </div>

          <Button
            type="primary"
            block
            size="large"
            onClick={handleSavePrice}
            style={{ height: '50px', borderRadius: '10px', fontWeight: 'bold', backgroundColor: '#22BABB', border: 'none', boxShadow: '0 4px 6px rgba(34, 186, 187, 0.2)' }}
          >
            CONFIRMAR PRECIO
          </Button>
        </div>
      </Drawer>
    </div>
  )
}

export default memo(ItemCardCarrito);